import React, { useEffect, useState } from "react";
import { apiUrl } from "../config/api";
import { userAuthHeaders } from "../utils/authHeaders";

const FILTERS = [
  { key: "today", label: "امروز" },
  { key: "yesterday", label: "یک روز قبل" },
  { key: "week", label: "یک هفته قبل" },
  { key: "all", label: "همه" },
];

function TredeDetail() {
  const ITEMS_PER_PAGE = 5;
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadTrades = async () => {
      const userData = localStorage.getItem("user");

      if (!userData) {
        setError("برای مشاهده جزئیات معاملات ابتدا وارد حساب کاربری شوید.");
        setLoading(false);
        return;
      }

      try {
        const user = JSON.parse(userData);

        if (!user?.id) {
          throw new Error("شناسه کاربر در دسترس نیست.");
        }

        const response = await fetch(apiUrl("/api/trades?limit=100"), {
          headers: userAuthHeaders(),
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "خطا در دریافت لیست معاملات");
        }

        setTrades(data.trades || []);
        setError("");
      } catch (fetchError) {
        setTrades([]);
        setError(fetchError.message || "خطا در دریافت لیست معاملات");
      } finally {
        setLoading(false);
      }
    };

    loadTrades();
  }, []);

  const formatNumber = (value, maximumFractionDigits = 2) => {
    if (value === null || value === undefined || value === "") return "-";

    return Number(value).toLocaleString("fa-IR", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    });
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("fa-IR");
  };

  const getTradeTypeText = (mode) => (mode === "buy" ? "خرید از ما" : "فروش به ما");

  const getStatusText = (status) => {
    switch (status) {
      case "approved":
        return "تایید شده";
      case "rejected":
        return "رد شده";
      case "pending":
        return "در انتظار";
      default:
        return status || "-";
    }
  };

  const isInFilterRange = (tradeDate) => {
    const tradeTime = new Date(tradeDate);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - 7);

    switch (activeFilter) {
      case "today":
        return tradeTime >= startOfToday;
      case "yesterday":
        return tradeTime >= startOfYesterday && tradeTime < startOfToday;
      case "week":
        return tradeTime >= startOfWeek;
      case "all":
      default:
        return true;
    }
  };

  const filteredTrades = trades.filter((trade) => isInFilterRange(trade.createdAt));
  const totalPages = Math.max(1, Math.ceil(filteredTrades.length / ITEMS_PER_PAGE));
  const paginatedTrades = filteredTrades.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <section className="MainPageTradesSection">
      <div className="MainPageSectionHeading">
        <span className="MainPagePanelEyebrow">معاملات</span>
        <h3 className="MainPagePanelTitle">جزئیات معاملات شما</h3>
      </div>

      <div className="UserPortalPanel">
        <div className="TredeDetailToolbar">
          <div className="TredeDetailFilters">
            {FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={activeFilter === filter.key ? "active" : ""}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <span className="TredeDetailCount">{filteredTrades.length} معامله</span>
        </div>

        {loading ? (
          <p className="UserPortalEmpty">در حال دریافت معاملات...</p>
        ) : error ? (
          <p className="UserPortalEmpty UserPortalError">{error}</p>
        ) : filteredTrades.length === 0 ? (
          <p className="UserPortalEmpty">معامله‌ای برای این بازه زمانی پیدا نشد.</p>
        ) : (
          <>
            <div className="UserPortalTableWrap TredeDetailDesktopTable">
              <table className="UserPortalTable">
                <thead>
                  <tr>
                    <th>نوع</th>
                    <th>وزن</th>
                    <th>مبلغ کل</th>
                    <th>نرخ پایه</th>
                    <th>وضعیت</th>
                    <th>تاریخ</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTrades.map((trade) => {
                    const baseRate = trade.mode === "buy" ? trade.valueBuy : trade.valueSell;

                    return (
                      <tr key={trade.id}>
                        <td data-label="نوع">{getTradeTypeText(trade.mode)}</td>
                        <td data-label="وزن">{formatNumber(trade.weight, 4)} گرم</td>
                        <td data-label="مبلغ کل">{formatNumber(trade.totalPrice, 0)} تومان</td>
                        <td data-label="نرخ پایه">{formatNumber(baseRate, 0)} تومان</td>
                        <td data-label="وضعیت">
                          <span
                            className={
                              trade.status === "approved"
                                ? "TredeDetailBadge approved"
                                : trade.status === "rejected"
                                  ? "TredeDetailBadge rejected"
                                  : "TredeDetailBadge pending"
                            }
                          >
                            {getStatusText(trade.status)}
                          </span>
                        </td>
                        <td data-label="تاریخ">{formatDate(trade.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="TredeDetailMobileCards">
              {paginatedTrades.map((trade) => {
                const baseRate = trade.mode === "buy" ? trade.valueBuy : trade.valueSell;
                const statusIcon =
                  trade.status === "approved" ? "✓" : trade.status === "rejected" ? "✕" : "•";

                return (
                  <article key={trade.id} className="TredeDetailMobileCard">
                    <div className="TredeDetailMobileTop">
                      <div className="TredeDetailMobileMain">
                        <span
                          className={
                            trade.status === "approved"
                              ? "TredeDetailMobileStatus approved"
                              : trade.status === "rejected"
                                ? "TredeDetailMobileStatus rejected"
                                : "TredeDetailMobileStatus pending"
                          }
                        >
                          {statusIcon}
                        </span>
                        <div className="TredeDetailMobileTypeWrap">
                          <strong className="TredeDetailMobileType">{getTradeTypeText(trade.mode)}</strong>
                          <span className="TredeDetailMobileRate">
                            ({formatNumber(baseRate, 0)} تومان)
                          </span>
                        </div>
                      </div>

                      <div className="TredeDetailMobileValues">
                        <div>
                          <span>وزن</span>
                          <strong>{formatNumber(trade.weight, 4)} گرم</strong>
                        </div>
                        <div>
                          <span>مبلغ کل</span>
                          <strong>{formatNumber(trade.totalPrice, 0)} تومان</strong>
                        </div>
                      </div>
                    </div>

                    <div className="TredeDetailMobileBottom">
                      <span className="TredeDetailMobileDate">{formatDate(trade.createdAt)}</span>
                    </div>
                  </article>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="TredeDetailPagination">
                <button
                  type="button"
                  className="TredeDetailPageBtn"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  قبلی
                </button>

                <span className="TredeDetailPageInfo">
                  صفحه {currentPage} از {totalPages}
                </span>

                <button
                  type="button"
                  className="TredeDetailPageBtn"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  بعدی
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default TredeDetail;
